import React from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { 
  FileText, 
  Lightbulb, 
  TrendingUp, 
  Users, 
  Bookmark,
  Target,
  Star
} from 'lucide-react'

interface Template {
  id: string
  name: string
  description: string
  icon: React.ReactNode
  category: string
  content: string
  estimatedTime: number
}

const templates: Template[] = [
  {
    id: 'guide',
    name: 'Guide pratique',
    description: 'Un guide étape par étape pour expliquer un processus',
    icon: <Bookmark className="w-5 h-5" />,
    category: 'Guide',
    estimatedTime: 8,
    content: `# Guide pratique : [Titre de votre guide]

## Introduction
Pourquoi ce guide est-il important ? Quel problème résout-il ?

## Prérequis
Ce dont vos lecteurs ont besoin avant de commencer :
- Élément 1
- Élément 2
- Élément 3

## Étape 1 : [Titre de l'étape]
Description détaillée de la première étape...

### Points clés :
- Point important 1
- Point important 2

## Étape 2 : [Titre de l'étape]
Description détaillée de la deuxième étape...

## Étape 3 : [Titre de l'étape]
Description détaillée de la troisième étape...

## Conseils et bonnes pratiques
- Conseil 1
- Conseil 2
- Conseil 3

## Conclusion
Résumé des points essentiels et prochaines étapes.

---
*N'hésitez pas à nous contacter si vous avez des questions !*`
  },
  {
    id: 'case-study',
    name: 'Étude de cas',
    description: 'Présentez un projet ou une réussite client',
    icon: <Target className="w-5 h-5" />,
    category: 'Business',
    estimatedTime: 10,
    content: `# Étude de cas : [Nom du projet]

## Le défi
Décrivez le problème ou l'objectif initial du client.

### Contexte
- Secteur d'activité
- Taille de l'entreprise
- Situation initiale

## Notre approche
Comment avez-vous abordé le projet ?

### Méthodologie
1. Phase de découverte
2. Stratégie développée
3. Mise en œuvre
4. Suivi et optimisation

## Solutions mises en place
Détaillez les solutions concrètes apportées :

### Solution 1 : [Nom]
Description et impact...

### Solution 2 : [Nom]
Description et impact...

## Résultats obtenus
Présentez les résultats chiffrés et qualitatifs.

### Métriques clés
- **Résultat 1** : +X%
- **Résultat 2** : X unités
- **Résultat 3** : X€ économisés

## Témoignage client
> "Citation du client sur les résultats obtenus..."
> 
> *— Nom, Poste, Entreprise*

## Conclusion
Leçons apprises et recommandations pour des projets similaires.`
  },
  {
    id: 'thought-leadership',
    name: 'Article d\'opinion',
    description: 'Partagez votre expertise et vision sur un sujet',
    icon: <Lightbulb className="w-5 h-5" />,
    category: 'Opinion',
    estimatedTime: 12,
    content: `# [Titre accrocheur de votre opinion]

## Introduction
Posez le contexte et l'enjeu dont vous allez parler.

## Le constat actuel
Décrivez la situation actuelle du marché/secteur.

### Les défis identifiés
- Défi 1
- Défi 2
- Défi 3

## Ma vision
Présentez votre point de vue unique et argumenté.

### Pourquoi cette approche ?
Argumentez votre position avec :
- Des exemples concrets
- Des données de marché
- Votre expérience

## Les opportunités à saisir
Identifiez les opportunités que vous percevez :

1. **Opportunité 1** : Description et impact potentiel
2. **Opportunité 2** : Description et impact potentiel
3. **Opportunité 3** : Description et impact potentiel

## Recommandations pratiques
Donnez des conseils actionnables :

### Pour les entreprises
- Action 1
- Action 2
- Action 3

### Pour les professionnels
- Action 1
- Action 2
- Action 3

## Conclusion
Synthèse de votre vision et appel à l'action.

---
*Qu'en pensez-vous ? Partagez votre point de vue en commentaire !*`
  },
  {
    id: 'trends',
    name: 'Tendances & Analyse',
    description: 'Analysez les tendances de votre secteur',
    icon: <TrendingUp className="w-5 h-5" />,
    category: 'Analyse',
    estimatedTime: 15,
    content: `# Tendances 2024 : [Votre secteur]

## Introduction
Pourquoi ces tendances sont-elles importantes à suivre ?

## Méthodologie de recherche
Comment avez-vous identifié ces tendances ?
- Sources utilisées
- Période d'analyse
- Critères de sélection

## Tendance #1 : [Nom de la tendance]

### Description
Explication détaillée de la tendance...

### Chiffres clés
- Statistique 1
- Statistique 2
- Statistique 3

### Impact sur le secteur
Comment cette tendance transforme-t-elle le marché ?

### Exemples concrets
- Exemple 1
- Exemple 2

## Tendance #2 : [Nom de la tendance]
[Même structure que la tendance 1]

## Tendance #3 : [Nom de la tendance]
[Même structure que la tendance 1]

## Analyse transversale
Comment ces tendances s'articulent-elles entre elles ?

### Synergies identifiées
- Synergie 1
- Synergie 2

### Tensions potentielles
- Tension 1
- Tension 2

## Implications pour les entreprises

### À court terme (6-12 mois)
- Action 1
- Action 2

### À moyen terme (1-3 ans)
- Action 1
- Action 2

### À long terme (3+ ans)
- Action 1
- Action 2

## Conclusion
Synthèse et recommandations stratégiques.

---
*Téléchargez notre rapport complet sur les tendances 2024*`
  },
  {
    id: 'interview',
    name: 'Interview expert',
    description: 'Format d\'interview avec un expert du secteur',
    icon: <Users className="w-5 h-5" />,
    category: 'Interview',
    estimatedTime: 6,
    content: `# Interview : [Nom de l'expert] sur [Sujet]

## Présentation de l'invité
**[Nom de l'expert]** est [description de son rôle et expertise]. 

### Son parcours
- Point marquant 1
- Point marquant 2
- Point marquant 3

---

## L'interview

**Pouvez-vous vous présenter et nous parler de votre parcours ?**

> Réponse de l'expert...

**Quelle est votre vision de [sujet] aujourd'hui ?**

> Réponse de l'expert...

**Quels sont selon vous les principaux défis du secteur ?**

> Réponse de l'expert...

**Quelles tendances observez-vous actuellement ?**

> Réponse de l'expert...

**Quels conseils donneriez-vous aux entreprises qui débutent ?**

> Réponse de l'expert...

**Comment voyez-vous l'évolution du secteur dans les 5 prochaines années ?**

> Réponse de l'expert...

**Un dernier conseil pour nos lecteurs ?**

> Réponse de l'expert...

---

## En résumé
Les points clés à retenir de cet échange :

1. **Point clé 1** : Explication
2. **Point clé 2** : Explication  
3. **Point clé 3** : Explication

## Pour aller plus loin
- Ressource 1
- Ressource 2
- Contact de l'expert : [email/LinkedIn]

---
*Merci à [Nom] pour cet échange enrichissant !*`
  }
]

interface ArticleTemplatesProps {
  onSelectTemplate: (content: string, title: string, estimatedTime: number) => void
}

export const ArticleTemplates: React.FC<ArticleTemplatesProps> = ({ onSelectTemplate }) => {
  const categories = [...new Set(templates.map(t => t.category))]

  const handleSelectTemplate = (template: Template) => {
    onSelectTemplate(
      template.content,
      template.name,
      template.estimatedTime
    )
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Templates d'articles
        </h2>
        <p className="text-gray-600">
          Choisissez un template pour démarrer votre rédaction plus rapidement
        </p>
      </div>

      {categories.map(category => (
        <div key={category} className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            {category}
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates
              .filter(template => template.category === category)
              .map(template => (
                <Card 
                  key={template.id}
                  className="hover:shadow-md transition-shadow cursor-pointer group"
                  onClick={() => handleSelectTemplate(template)}
                >
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-3 text-base group-hover:text-blue-600 transition-colors">
                      <div className="p-2 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                        {template.icon}
                      </div>
                      {template.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-3">
                      {template.description}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        ~{template.estimatedTime} min de lecture
                      </span>
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-xs">
                        Utiliser
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
        </div>
      ))}
    </div>
  )
} 